import type { SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import { ClassModel, action, getParentOfType, register, types } from "@gadgetinc/mobx-quick-tree";
import { compact, isNull } from "lodash";
import { AppTag } from "state-trees/src/tags";
import { assert } from "state-trees/src/utils";
import { Area, Properties, trackEvent } from "../../lib/eventTracker";
import { reportFrontendErrorForInvestigation } from "../../lib/reportFrontendErrorForInvestigation";

const tagWith = (value: any, tag: string) => (value ? `${tag}:${value}` : undefined);

const GadgetSurveyCompletedKey = "gadget:surveyCompleted";

export const InitialSurveySteps: SnapshotIn<typeof SurveyStep>[] = [
  {
    key: "technicalLevel",
  },
];

export const StepConfigs: Record<
  string,
  {
    eventClass: string;
    getTags: (step: SurveyStep) => Record<string, any>;
    submittable: (step: SurveyStep) => boolean;
    required?: boolean;
  }
> = {
  experience: {
    eventClass: "Experience",
    getTags(step) {
      return {
        experience_tag: tagWith(step.answers.get("experience"), AppTag.Experience),
      };
    },
    submittable(step) {
      return !!step.answers.get("experience");
    },
  },
  organization: {
    eventClass: "Organization",
    required: true,
    getTags: (step) => ({
      organization_tag: tagWith(step.answers.get("organization"), AppTag.OrgPref),
      custom_organization_tag: tagWith(step.answers.get("customOrganization"), AppTag.OrgPref),
    }),
    submittable(step) {
      return !!step.answers.get("organization") || !!step.answers.get("customOrganization");
    },
  },
  discovery: {
    eventClass: "Discovery",
    getTags: (step) => ({
      discovery_tag: tagWith(step.answers.get("discovery"), AppTag.Discovery),
      custom_discovery_tag: tagWith(step.answers.get("customDiscovery"), AppTag.Discovery),
    }),
    submittable(step) {
      return !!step.answers.get("discovery") || !!step.answers.get("customDiscovery");
    },
  },
  technicalLevel: {
    eventClass: "TechnicalLevel",
    getTags: (step) => ({
      technical_level_tag: tagWith(step.answers.get("technicalLevel"), AppTag.TechnicalLevel),
    }),
    submittable(step) {
      return !!step.answers.get("technicalLevel");
    },
  },
};

@register
export class SurveyStep extends ClassModel({
  key: types.string,
  answers: types.map(types.string),
}) {
  get config() {
    return StepConfigs[this.key];
  }

  get eventClass() {
    return this.config.eventClass;
  }

  get canSubmit() {
    return this.config.submittable(this);
  }

  get required() {
    return this.config.required ?? false;
  }

  userId() {
    return this.survey.userId ?? undefined;
  }

  tags() {
    return this.config.getTags(this);
  }

  get survey() {
    return getParentOfType(this, Survey);
  }

  @action
  submit() {
    this.survey.moveToNextStep();
  }

  @action
  skip() {
    if (this.required) {
      return;
    }
    this.survey.moveToNextStep(true);
  }

  @action
  setAnswer(key: string, value: string) {
    this.answers.set(key, value);
  }
}

@register
export class Survey extends ClassModel({
  steps: types.array(SurveyStep),
  stepIndex: types.maybeNull(types.number),
  userId: types.string,
  trackable: types.optional(types.boolean, true),
}) {
  get step() {
    if (typeof window !== "undefined" && window.localStorage.getItem(GadgetSurveyCompletedKey) == "true") return null;
    if (isNull(this.stepIndex)) return null;
    return this.steps[this.stepIndex];
  }

  @action
  trackEvent(name: string, properties: Properties, area?: Area) {
    if (this.trackable) {
      trackEvent(name, properties, area);
    }
  }

  @action
  moveToNextStep(isSkip = false) {
    const step = assert(this.step, "No survey step to move on from");
    const eventName = `${step.eventClass} Survey ${isSkip ? "Skipped" : "Submitted"}`;

    const tags = step.tags();

    this.trackEvent(eventName, { user_id: this.userId ?? undefined, ...tags }, "auth");

    fetch("/auth/api/add-user-tags", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tags: [],
        createIfNotExistsMatches: Object.fromEntries(compact(Object.values(tags)).map((tag) => [tag.split(":")[0], tag])),
      }),
    }).catch(reportFrontendErrorForInvestigation);

    const nextIndex = (this.stepIndex ?? 0) + 1;
    if (nextIndex >= this.steps.length) {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(GadgetSurveyCompletedKey, "true");
      }

      this.stepIndex = null;
    } else {
      this.stepIndex = nextIndex;
    }
  }
}
